import React from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, FileText, Users } from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { CFA_INFO } from '../data/constants';
const CGV_VERSION = 'Version 2';
const CGV_DATE = '3 août 2026';
function Article({
  number,
  title,
  children
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return <section className="mt-14 first:mt-0" data-id="element-1194" data-mp="3udhqfknh63tq">
      <div className="flex items-center gap-4 mb-6" data-id="element-1195" data-mp="3ue9iwmlrzlfs">
        <span className="w-9 h-9 shrink-0 rounded-lg bg-brand-orange text-white font-extrabold font-heading flex items-center justify-center" data-id="element-1196" data-mp="3pfg8x45p6ckd">
          {number}
        </span>
        <h2 className="text-xl md:text-2xl font-heading font-extrabold text-primary-dark uppercase tracking-tight" data-id="element-1197" data-mp="37q12nhiuv7z">
          {title}
        </h2>
      </div>
      <div className="border-t-2 border-brand-orange/30 pt-6 space-y-4" data-id="element-1198" data-mp="1imvuoblxuxfj">
        {children}
      </div>
    </section>;
}
function SubHeading({
  children
}: {
  children: React.ReactNode;
}) {
  return <h3 className="text-base font-bold text-primary mt-6 first:mt-0" data-id="element-1199" data-mp="uc7mozejgoct">
      {children}
    </h3>;
}
function P({
  children
}: {
  children: React.ReactNode;
}) {
  return <p className="text-base leading-relaxed text-slate-700" data-id="element-1200" data-mp="13egs5auy9rjc">{children}</p>;
}
function Panel({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <div className="bg-white border-l-4 border-brand-orange rounded-r-xl shadow-sm p-6 space-y-3" data-id="element-1201" data-mp="bmrfjt8clxnf">
      <h4 className="font-bold text-primary-dark" data-id="element-1202" data-mp="fnbaqr9d2r6x">{title}</h4>
      {children}
    </div>;
}
function DarkPanel({
  title,
  children
}: {
  title: string;
  children: React.ReactNode;
}) {
  return <div className="bg-primary-dark text-white rounded-2xl p-6 space-y-3 border-l-4 border-brand-orange" data-id="element-1203" data-mp="pdw9qfsy5xqd">
      <h4 className="font-bold font-heading" data-id="element-1204" data-mp="3o4egr6whc2j0">{title}</h4>
      <div className="text-lavande space-y-3 [&_p]:text-lavande [&_p]:leading-relaxed" data-id="element-1205" data-mp="3o1sarbp8lv2w">
        {children}
      </div>
    </div>;
}
function Highlight({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="bg-primary text-white rounded-2xl p-6 font-bold leading-relaxed" data-id="element-1206" data-mp="v9eckx5pxhd1">
      {children}
    </div>;
}
function Bullets({
  items
}: {
  items: string[];
}) {
  return <ul className="space-y-2" data-id="element-1207" data-mp="192ds2z5i3bkl">
      {items.map(item => <li key={item} className="flex gap-3 text-slate-700 leading-relaxed" data-id="element-1208" data-mp="1508yg0f1yexy">
          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-brand-orange shrink-0" data-id="element-1209" data-mp="h8li2cff2m9x" />
          <span data-id="element-1210" data-mp="cato7cv7oq33">{item}</span>
        </li>)}
    </ul>;
}
export function CGV() {
  return <div className="flex flex-col w-full bg-muted min-h-screen" data-id="element-1211" data-mp="qakxx1ep3kba">
      <PageHeader title="Conditions Générales de Vente" subtitle={`${CGV_VERSION} — Mise à jour : ${CGV_DATE} — Formations dispensées dans le cadre de l'apprentissage`} data-id="element-1212" data-mp="q7oz7av0wrls" />

      <div className="container max-w-4xl mx-auto py-16 md:py-24 space-y-8" data-id="element-1213" data-mp="21rb78tef0s94">
        {/* Cartouche d'identification */}
        <div className="grid md:grid-cols-2 gap-6" data-id="element-1214" data-mp="2575qv496kw7j">
          <div className="bg-white border-l-4 border-brand-orange rounded-r-2xl shadow-sm p-6" data-id="element-1215" data-mp="255kv5bb4r783">
            <h2 className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-wider text-primary-dark mb-4" data-id="element-1216" data-mp="299iy5x3kt26f">
              <FileText className="w-4 h-4 text-brand-orange" data-id="element-1217" data-mp="1vb2r251sx9nr" />
              Informations du document
            </h2>
            <p className="font-bold text-primary-dark" data-id="element-1218" data-mp="2nxneiuf5p1m1">{CGV_VERSION}</p>
            <p className="text-slate-700 leading-relaxed" data-id="element-1219" data-mp="2nyf9c30xivb9">
              Mise à jour : {CGV_DATE}
              <br data-id="element-1220" data-mp="2svxerjfqmsei" />
              Application : formations par apprentissage
              <br data-id="element-1221" data-mp="2e7173nhlfhef" />
              Public : entreprises, apprentis et parties contractantes
            </p>
          </div>

          <div className="bg-lavande border-l-4 border-primary rounded-r-2xl p-6" data-id="element-1222" data-mp="2j52lvw7zkuk3">
            <h2 className="text-sm font-extrabold uppercase tracking-wider text-primary-dark mb-4" data-id="element-1223" data-mp="36ux1umnlqz8k">
              Identification du CFA
            </h2>
            <p className="text-primary-dark leading-relaxed" data-id="element-1224" data-mp="1n158g04jupi8">
              <strong data-id="element-1225" data-mp="1mzaoao13r1f7">{CFA_INFO.name}</strong>
              <br data-id="element-1226" data-mp="1iza93166im73" />
              {CFA_INFO.address}
              <br data-id="element-1227" data-mp="1j1wi758px9rf" />
              SIRET : {CFA_INFO.siret}
              <br data-id="element-1228" data-mp="1e1s0jbw2q8zm" />
              UAI : {CFA_INFO.uai}
              <br data-id="element-1229" data-mp="1e2ju6h641wn9" />
              Déclaration d'activité enregistrée sous le numéro {CFA_INFO.nda}{' '}
              auprès du préfet de la région Bourgogne-Franche-Comté. Cet
              enregistrement ne vaut pas agrément de l'État.
              <br data-id="element-1230" data-mp="21q1k075teknl" />
              {CFA_INFO.phone}
              <br data-id="element-1231" data-mp="21snnnvpl4g0j" />
              {CFA_INFO.email}
            </p>
          </div>
        </div>

        <div className="bg-white border-l-4 border-brand-orange rounded-r-2xl shadow-sm p-6" data-id="element-1232" data-mp="1wxi2x07x57e1">
          <h2 className="flex items-center gap-2 text-sm font-extrabold uppercase tracking-wider text-primary-dark mb-4" data-id="element-1233" data-mp="1rydwghcdpnre">
            <Users className="w-4 h-4 text-brand-orange" data-id="element-1234" data-mp="2kp5or3qbps9g" />
            Document applicable aux
          </h2>
          <Bullets items={['Entreprises privées', 'Employeurs publics', 'Apprentis', 'Stagiaires de la formation professionnelle', 'Parties contractantes']} data-id="element-1235" data-mp="2s0k1q6ccv22n" />
        </div>

        {/* Corps des CGV */}
        <div className="bg-white p-8 md:p-12 rounded-3xl shadow-xl" data-id="element-1236" data-mp="36y4373a1mf42">
          <Article number={1} title="Objet et acceptation" data-id="element-1237" data-mp="36v861h60rugo">
            <P data-id="element-1238" data-mp="3aymw70no9r7u">
              Les présentes Conditions Générales de Vente (CGV) définissent les
              règles applicables aux formations dispensées par le CFA Zillenium
              Alternance dans le cadre de l'apprentissage. Elles s'appliquent à
              l'ensemble des parties contractantes (entreprise, apprenti, et le
              cas échéant, représentant légal).
            </P>
            <P data-id="element-1239" data-mp="3uoqa2r8b9tch">
              Les présentes CGV sont communiquées à l'entreprise, à l'apprenti
              et, le cas échéant, à son représentant légal avant la conclusion
              de la convention de formation. La signature de la convention, du
              devis ou de tout document contractuel visant expressément les
              présentes CGV vaut acceptation de celles-ci dans leur version
              applicable à la date de signature.
            </P>
          </Article>

          <Article number={2} title="Entrée en formation et statut SFP" data-id="element-1240" data-mp="23ksmsuj4g4gp">
            <SubHeading data-id="element-1241" data-mp="2i9ysn1cgssxk">2.1 Entrée sous contrat d'apprentissage</SubHeading>
            <P data-id="element-1242" data-mp="2dbdsf1x7q994">
              Conformément aux dispositions du Code du travail, le contrat
              d'apprentissage peut débuter dans un délai maximal de trois mois
              avant le commencement effectif des enseignements dispensés par le
              CFA. Une personne remplissant les conditions d'accès à
              l'apprentissage peut, lorsqu'elle n'a pas encore été engagée par
              un employeur, débuter le cycle de formation sous statut de
              stagiaire de la formation professionnelle, dans la limite de trois
              mois, afin de conclure un contrat d'apprentissage.
            </P>
            <div className="grid md:grid-cols-2 gap-6 pt-2" data-id="element-1243" data-mp="mtgru6j5lcse">
              <Panel title="2.2 Entrée sans employeur (SFP)" data-id="element-1244" data-mp="91ts1wq05m4v">
                <P data-id="element-1245" data-mp="91ajv7q5mc41">
                  L'admission sans employeur, sous statut de stagiaire de la
                  formation professionnelle, est soumise à la validation du CFA,
                  aux places disponibles et à la faisabilité du projet, pour une
                  durée maximale de trois mois.
                </P>
                <P data-id="element-1246" data-mp="40ct46r2w9qx">
                  La conclusion d'un contrat d'apprentissage reste nécessaire
                  pour poursuivre normalement la formation après la période
                  autorisée.
                </P>
                <P data-id="element-1247" data-mp="1kh8pf1m4ig0i">
                  Le CFA accompagne le jeune dans la recherche d'un employeur,
                  sans garantie de résultat.
                </P>
              </Panel>
              <Panel title="2.3 Maintien après rupture" data-id="element-1248" data-mp="2y0hn6volkjro">
                <P data-id="element-1249" data-mp="2xzzjxf3ip5a9">
                  Le maintien en formation pendant une durée maximale de six
                  mois après la rupture du contrat s'effectue dans les
                  conditions prévues par la réglementation et sous réserve des
                  modalités de financement applicables.
                </P>
                <P data-id="element-1250" data-mp="2k7041bztp926">
                  Le CFA accompagne la transition administrative et pédagogique
                  de l'apprenti concerné ainsi que sa recherche d'un nouvel
                  employeur, sans garantie de résultat.
                </P>
              </Panel>
            </div>
          </Article>

          <Article number={3} title="Gratuité pour l'apprenti" data-id="element-1251" data-mp="25dn4a37fhsgb">
            <Highlight data-id="element-1252" data-mp="21bt7i60ct8lf">
              Aucun frais pédagogique, d'inscription, administratif ou d'accès
              aux enseignements ne peut être facturé à l'apprenti au titre de la
              formation suivie dans le cadre du contrat d'apprentissage.
            </Highlight>
            <P data-id="element-1253" data-mp="1hffiebfff9lj">
              Les éventuels frais annexes distincts de la formation, comme le
              transport, l'hébergement, la restauration, la mobilité ou
              l'équipement, sont traités séparément selon les dispositifs
              applicables et les accords conclus.
            </P>
            <DarkPanel title="À retenir" data-id="element-1254" data-mp="1v6lmylxkbxfa">
              <p data-id="element-1255" data-mp="1v9pxe11mfpee">
                Aucun coût pédagogique ne peut être demandé à l'apprenti. Les
                éventuels frais annexes sont traités séparément selon les
                dispositifs applicables et les accords conclus.
              </p>
            </DarkPanel>
          </Article>

          <Article number={4} title="Modalités de financement" data-id="element-1256" data-mp="17knk15e0sipp">
            <div className="grid md:grid-cols-2 gap-6" data-id="element-1257" data-mp="1ci6zg8mgf1j8">
              <Panel title="4.1 Financement par l'OPCO" data-id="element-1258" data-mp="2ixpxhrktos5n">
                <P data-id="element-1259" data-mp="2ix7vsffe5lqc">
                  Le financement est assuré par l'opérateur de compétences
                  compétent, selon le niveau de prise en charge applicable à la
                  certification, à la branche professionnelle et à la date de
                  conclusion du contrat. Les niveaux de prise en charge sont
                  déterminés dans le cadre réglementaire applicable et publiés
                  dans le référentiel national tenu par France compétences.
                </P>
                <P data-id="element-1260" data-mp="20wqws5w1xhya">
                  L'entreprise s'engage à effectuer les démarches
                  d'enregistrement du contrat auprès de son OPCO au plus tard
                  dans les cinq jours ouvrables suivant le début de l'exécution
                  du contrat.
                </P>
              </Panel>
              <Panel title="4.2 Reste à charge éventuel" data-id="element-1261" data-mp="1d7g3f9h16pu6">
                <P data-id="element-1262" data-mp="1h7ghgt07wz0p">
                  Si le NPEC est inférieur au coût pédagogique publié par le
                  CFA, un reste à charge peut être facturé à l'entreprise,
                  conformément à la convention de formation conclue.
                </P>
                <P data-id="element-1263" data-mp="gy8nphogprdb">
                  Aucun reste à charge ne peut être facturé à l'entreprise sans
                  avoir été préalablement porté à sa connaissance et
                  expressément accepté dans la convention de formation, un
                  devis, un avenant ou tout autre document contractuel signé.
                </P>
                <P data-id="element-1264" data-mp="36kepn9ifj0k">
                  Toute modification du NPEC par les autorités compétentes
                  s'applique de plein droit aux contrats conclus postérieurement
                  à la date d'effet de cette modification.
                </P>
              </Panel>
            </div>
            <SubHeading data-id="element-1265" data-mp="3ldhza6rzauxo">4.3 Cas des employeurs publics</SubHeading>
            <P data-id="element-1266" data-mp="3ge5b9ipdfhti">
              Pour les employeurs du secteur public, le financement s'effectue
              selon les dispositions légales et réglementaires spécifiques
              (notamment via le CNFPT pour la fonction publique territoriale).
              L'employeur public s'engage à garantir le financement de la
              formation selon les modalités définies dans la convention de
              formation. Les modalités de facturation sont adaptées aux règles
              de la comptabilité publique (Chorus Pro).
            </P>
          </Article>

          <Article number={5} title="Mandat de dépôt" data-id="element-1267" data-mp="1oocre7kc0ih1">
            <P data-id="element-1268" data-mp="1tq23lqjaj26p">
              Pour faciliter les démarches administratives, l'entreprise peut
              mandater le CFA Zillenium Alternance pour procéder au dépôt du
              contrat d'apprentissage et de la convention de formation auprès de
              l'OPCO compétent.
            </P>
            <P data-id="element-1269" data-mp="27fvqw4f5deeb">
              Ce mandat est accordé à titre gratuit. Il ne décharge pas
              l'entreprise de sa responsabilité quant à l'exactitude des
              informations transmises et à la signature des documents
              contractuels.
            </P>
          </Article>

          <Article number={6} title="Facturation et règlement" data-id="element-1270" data-mp="111mt9hb3t81p">
            <P data-id="element-1271" data-mp="tgu0vppk4k76">
              La facturation est émise conformément à l'échéancier prévu par la
              réglementation et les règles de l'OPCO financeur (généralement
              sous forme d'acomptes liés à la présence de l'apprenti). En cas de
              reste à charge pour l'entreprise, la facturation est émise selon
              l'échéancier défini dans la convention de formation.
            </P>
            <DarkPanel title="Modalités de paiement" data-id="element-1272" data-mp="9sbh5nnblyzv">
              <p data-id="element-1273" data-mp="9r0fbtdfr1be">
                Les règlements effectués par l'OPCO sont soumis aux règles
                propres de l'opérateur de compétences.
              </p>
              <p data-id="element-1274" data-mp="jjo7gath0h3s">
                Les dispositions ci-dessous s'appliquent exclusivement aux
                factures de reste à charge adressées directement à l'entreprise
                : les factures sont payables à réception, sauf délai spécifique
                accordé. En cas de retard de paiement, des pénalités de retard
                calculées sur la base de trois fois le taux d'intérêt légal en
                vigueur, ainsi qu'une indemnité forfaitaire pour frais de
                recouvrement de 40 euros, sont exigibles de plein droit dès le
                jour suivant la date de règlement figurant sur la facture.
              </p>
            </DarkPanel>
          </Article>

          <Article number={7} title="Documents contractuels" data-id="element-1275" data-mp="efx5rzjklf8r">
            <P data-id="element-1276" data-mp="igz50gdhlraq">L'engagement entre les parties est formalisé par :</P>
            <Bullets items={['Le contrat d\'apprentissage (CERFA n° 10103*10 ou version en vigueur) ;', 'La convention de formation conclue entre le CFA et l\'employeur ;', 'Les présentes Conditions Générales de Vente ;', 'Le règlement intérieur du CFA (remis à l\'apprenti et consultable sur demande) ;', 'Le calendrier de formation (alternance) et le programme pédagogique.']} data-id="element-1277" data-mp="iitm1jln0z9j" />
            <P data-id="element-1278" data-mp="2z77dzyuuqdxq">
              En cas de contradiction, les dispositions particulières de la
              convention de formation, du devis ou d'un avenant signé prévalent
              sur les présentes CGV. Les dispositions légales et réglementaires
              d'ordre public demeurent applicables en priorité.
            </P>
          </Article>

          <Article number={8} title="Obligations des parties" data-id="element-1279" data-mp="12jwgik0uob6x">
            <DarkPanel title="8.1 Obligations du CFA" data-id="element-1280" data-mp="1z8ud87s5uj6q">
              <p data-id="element-1281" data-mp="1z8knmlb12e1k">
                Zillenium Alternance s'engage notamment à dispenser la formation
                conformément au référentiel applicable, assurer le suivi
                pédagogique, organiser les évaluations, assurer les échanges
                avec l'entreprise, informer l'apprenti et l'employeur, inscrire
                l'apprenti aux épreuves lorsque les conditions sont réunies,
                mettre en œuvre les adaptations raisonnablement possibles pour
                les personnes en situation de handicap, et assurer une
                traçabilité du parcours.
              </p>
              <p className="font-bold text-white" data-id="element-1282" data-mp="b98p2zacl0ll">
                Le CFA est tenu à une obligation de moyens et non à une
                obligation de résultat.
              </p>
              <p data-id="element-1283" data-mp="2a54fqvj8d4z">
                Il ne garantit pas la réussite à l'examen, l'obtention d'un
                contrat, le maintien du contrat ou l'embauche à l'issue de la
                formation.
              </p>
            </DarkPanel>
            <div className="grid md:grid-cols-2 gap-6 pt-2" data-id="element-1284" data-mp="3tgy954pjaue9">
              <Panel title="8.2 Obligations de l'entreprise" data-id="element-1285" data-mp="3ti99svnonm15">
                <P data-id="element-1286" data-mp="3fqclz3t1ryak">L'entreprise s'engage notamment à :</P>
                <Bullets items={["désigner un maître d'apprentissage qualifié ;", 'confier des missions cohérentes avec la certification ;', "libérer l'apprenti pour les cours et examens ;", 'assurer l\'encadrement, participer aux bilans et suivis ;', 'transmettre les informations et documents demandés ;', 'respecter ses obligations sociales.']} data-id="element-1287" data-mp="3fnqfl7hw7otx" />
                <P data-id="element-1288" data-mp="313dvpc5xf9xf">
                  L'entreprise informe le CFA de toute modification susceptible
                  d'affecter l'exécution du contrat, notamment un changement de
                  maître d'apprentissage, d'adresse, d'horaires, de missions, de
                  convention collective, d'OPCO, de situation administrative ou
                  de date de rupture.
                </P>
              </Panel>
              <Panel title="8.3 Obligations de l'apprenti" data-id="element-1289" data-mp="3jsaqdh58k4g6">
                <P data-id="element-1290" data-mp="3eu982yfmw95r">L'apprenti s'engage notamment à :</P>
                <Bullets items={["faire preuve d'assiduité et de ponctualité ;", 'respecter le règlement intérieur et les consignes ;', 'participer aux évaluations et suivis pédagogiques ;', 'adopter un comportement professionnel ;', 'se présenter aux examens ;', "informer le CFA et l'employeur de toute absence ;", 'fournir les justificatifs demandés ;', 'restituer le matériel appartenant au CFA.']} data-id="element-1291" data-mp="3eurdolo6rzqc" />
                <P data-id="element-1292" data-mp="1cznusdr0fhje">
                  Les sanctions disciplinaires relèvent du règlement intérieur
                  et des règles applicables au contrat d'apprentissage.
                </P>
              </Panel>
            </div>
            <div className="bg-lavande border-l-4 border-primary rounded-r-xl p-6" data-id="element-1293" data-mp="pcfpvwxhuahw">
              <h4 className="text-sm font-extrabold uppercase tracking-wider text-primary-dark mb-2" data-id="element-1294" data-mp="pacoyhusywye">
                Coordination du parcours
              </h4>
              <p className="text-primary-dark leading-relaxed" data-id="element-1295" data-mp="3tpw27rsq7w4a">
                La réussite du parcours repose sur la coopération régulière
                entre le CFA, l'entreprise, le maître d'apprentissage et
                l'apprenti.
              </p>
            </div>
          </Article>

          <Article number={9} title="Calendrier et organisation" data-id="element-1296" data-mp="3laome5l798qm">
            <P data-id="element-1297" data-mp="3h7a4tahm4kb2">
              Le calendrier de l'alternance est communiqué à l'entreprise et à
              l'apprenti avant le début de la formation. Le CFA se réserve le
              droit de modifier ce calendrier, les lieux de formation ou les
              modalités pédagogiques (notamment le passage en distanciel) pour
              des raisons d'organisation, de force majeure ou d'évolution
              réglementaire. Les parties en sont informées dans les meilleurs
              délais.
            </P>
            <P data-id="element-1298" data-mp="2tj8q1jtuc7n7">
              Ces ajustements ne modifient pas le volume global de formation
              prévu, sauf nécessité réglementaire, accord écrit des parties ou
              avenant.
            </P>
          </Article>

          <Article number={10} title="Force majeure" data-id="element-1299" data-mp="2jkt8o8km8cqw">
            <P data-id="element-1300" data-mp="2ibya1q6p1iy3">
              La responsabilité du CFA Zillenium Alternance ne pourra être
              engagée si l'inexécution ou le retard dans l'exécution de l'une de
              ses obligations découle d'un cas de force majeure au sens de
              l'article 1218 du Code civil.
            </P>
          </Article>

          <Article number={11} title="Rupture du contrat" data-id="element-1301" data-mp="23dw5bcnxerf9">
            <P data-id="element-1302" data-mp="1zdc9ccf39tup">
              Le contrat d'apprentissage peut être rompu librement par l'une ou
              l'autre des parties jusqu'à l'échéance des quarante-cinq premiers
              jours, consécutifs ou non, de formation pratique en entreprise.
              Passé ce délai, la rupture intervient dans les conditions prévues
              par le Code du travail.
            </P>
            <Panel title="Conséquences financières" data-id="element-1303" data-mp="1folcqigqc3c7">
              <P data-id="element-1304" data-mp="1fobn4vzljy71">
                En cas de rupture, les sommes financées ou facturées sont
                régularisées conformément aux règles du financeur, au prorata
                temporis journalier de la durée effectivement exécutée et sur la
                base des documents contractuels applicables.
              </P>
            </Panel>
          </Article>

          <Article number={12} title="Responsabilité et assurances" data-id="element-1305" data-mp="5m2jcpd0ehj">
            <div className="grid md:grid-cols-2 gap-6" data-id="element-1306" data-mp="54ykpk5acxds">
              <Panel title="Responsabilité de l'entreprise" data-id="element-1307" data-mp="54gje93rnsz0">
                <P data-id="element-1308" data-mp="33n45p6u6sxbm">
                  Pendant le temps de présence en entreprise, l'apprenti est
                  placé sous la responsabilité de son employeur. L'entreprise
                  doit être couverte par une assurance responsabilité civile
                  professionnelle couvrant les risques liés à l'activité de
                  l'apprenti.
                </P>
              </Panel>
              <Panel title="Responsabilité du CFA" data-id="element-1309" data-mp="2kwmfpa0qofty">
                <P data-id="element-1310" data-mp="2ku0ctnosmoi2">
                  Pendant le temps de formation au CFA, l'apprenti est placé
                  sous la responsabilité du centre de formation. Le CFA décline
                  toute responsabilité en cas de perte, vol ou détérioration des
                  objets personnels de l'apprenti.
                </P>
              </Panel>
            </div>
          </Article>

          <Article number={13} title="Réclamations et médiation" data-id="element-1311" data-mp="2rjjg12993b6a">
            <P data-id="element-1312" data-mp="2mkyhr8dk71kh">
              Toute réclamation relative à la formation doit être adressée par
              écrit à la direction du CFA (
              <a href={`mailto:${CFA_INFO.email}`} className="text-brand-orange hover:underline font-medium" data-id="element-1313" data-mp="1yvxavpkdjyhb">
                {CFA_INFO.email}
              </a>
              ). Le CFA s'engage à accuser réception et à apporter une réponse
              dans des délais raisonnables.
            </P>
            <P data-id="element-1314" data-mp="3k8gvyypzovvv">
              Pour les différends opposant l'employeur et l'apprenti concernant
              l'exécution ou la rupture du contrat d'apprentissage, les parties
              peuvent saisir le médiateur de l'apprentissage compétent. Les
              autres réclamations relatives à la formation, à la facturation ou
              aux documents contractuels sont traitées selon les procédures
              amiables et juridictionnelles applicables.
            </P>
          </Article>

          <Article number={14} title="Accessibilité handicap" data-id="element-1315" data-mp="x015oudyegjl">
            <P data-id="element-1316" data-mp="x2dtvpab1n53">
              Le CFA Zillenium Alternance met en œuvre les moyens nécessaires
              pour accueillir et accompagner les personnes en situation de
              handicap. Un référent handicap est désigné au sein du CFA pour
              évaluer les besoins spécifiques et proposer des adaptations
              pédagogiques, matérielles ou organisationnelles (
              <a href="mailto:samir@zilleniumalternance.fr" className="text-brand-orange hover:underline font-medium" data-id="element-1317" data-mp="1fxkbzq911l2b">
                samir@zilleniumalternance.fr
              </a>
              ).
            </P>
          </Article>

          <Article number={15} title="Propriété intellectuelle" data-id="element-1318" data-mp="jvg9vmr5eoes">
            <Panel title="Supports et ressources pédagogiques" data-id="element-1319" data-mp="euacvcqggezg">
              <P data-id="element-1320" data-mp="ex4yv8p4a7uz">
                L'ensemble des supports pédagogiques, documents, outils et
                ressources mis à la disposition de l'apprenti et de l'entreprise
                par le CFA reste la propriété exclusive de Zillenium Alternance
                ou de ses partenaires. Toute reproduction, représentation,
                modification, publication, transmission, dénaturation, totale ou
                partielle, est strictement interdite sans l'accord préalable
                écrit du CFA.
              </P>
            </Panel>
          </Article>

          <Article number={16} title="Protection des données (RGPD)" data-id="element-1321" data-mp="osgdzs4fth39">
            <P data-id="element-1322" data-mp="1m9f6lbm9auje">
              Les données à caractère personnel collectées par le CFA sont
              nécessaires à la gestion administrative, pédagogique et financière
              de la formation, ainsi qu'au respect des obligations légales de
              suivi et de certification. Elles sont conservées pour la durée
              légale applicable.
            </P>
            <P data-id="element-1323" data-mp="1yr7uwlaxv4rb">
              Conformément au Règlement Général sur la Protection des Données
              (RGPD) et à la loi Informatique et Libertés, toute personne
              concernée dispose d'un droit d'accès, de rectification,
              d'effacement, de limitation, de portabilité et d'opposition au
              traitement de ses données. Ces droits s'exercent par demande
              écrite adressée au CFA.
            </P>
            <P data-id="element-1324" data-mp="2hhzai4lirre5">
              Les traitements reposent, selon leur nature, sur l'exécution des
              relations précontractuelles et contractuelles, le respect des
              obligations légales du CFA et, lorsque cela est applicable,
              l'intérêt légitime de Zillenium Alternance. Les données peuvent
              être transmises aux employeurs, financeurs, administrations,
              autorités certificatrices et prestataires habilités dans la limite
              de leurs missions. Toute demande peut être adressée à{' '}
              <a href={`mailto:${CFA_INFO.rgpdEmail}`} className="text-brand-orange hover:underline font-medium" data-id="element-1325" data-mp="154ionr8yssf9">
                {CFA_INFO.rgpdEmail}
              </a>
              . La personne concernée peut également introduire une réclamation
              auprès de la CNIL. Voir également notre{' '}
              <Link to="/confidentialite" className="text-brand-orange hover:underline font-medium" data-id="element-1326" data-mp="1a3lsx9h4eum6">
                politique de confidentialité
              </Link>
              .
            </P>
          </Article>

          <Article number={17} title="Validité, modification et litiges" data-id="element-1327" data-mp="3pu83435rac7f">
            <div className="grid md:grid-cols-2 gap-6" data-id="element-1328" data-mp="3kw6ihdsom4tu">
              <Panel title="Modification des CGV" data-id="element-1329" data-mp="3kxrcn2b33lr6">
                <P data-id="element-1330" data-mp="20ddw24m3x0aq">
                  Le CFA se réserve le droit de modifier les présentes CGV à
                  tout moment. Les nouvelles CGV s'appliqueront aux contrats
                  conclus postérieurement à leur date de mise à jour.
                </P>
              </Panel>
              <Panel title="Droit applicable et juridiction" data-id="element-1331" data-mp="1vmxfwcqwj0ko">
                <P data-id="element-1332" data-mp="1vlco2uvz1vqi">
                  Les présentes CGV sont soumises au droit français. À défaut de
                  résolution amiable, le litige relève des juridictions
                  matériellement et territorialement compétentes conformément
                  aux règles légales applicables.
                </P>
              </Panel>
            </div>
          </Article>

          <div className="mt-16 pt-8 border-t border-slate-200 flex items-start gap-3" data-id="element-1333" data-mp="1cp2zvercvoss">
            <AlertCircle className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" data-id="element-1334" data-mp="331sgwga9wuv8" />
            <p className="text-sm text-slate-600 font-semibold leading-relaxed" data-id="element-1335" data-mp="372ueksojkyv3">
              {CFA_INFO.name} — {CFA_INFO.legalName} — UAI {CFA_INFO.uai} —
              Conditions Générales de Vente — {CGV_VERSION}, {CGV_DATE}
            </p>
          </div>
        </div>
      </div>
    </div>;
}